/**
 * Locates what actually differs between the server-rendered markup and the
 * DOM React produced after recovering from a hydration mismatch (US-11224).
 *
 * Client-safe — no server dependencies; used by `entry.client.tsx`.
 */

/**
 * Removes `<script>` elements (tags and body) from a markup string.
 *
 * Scripts are never user-visible content, and React's recovery render
 * relocates React Router's inline history-restoration script to the top of
 * `<body>` — without stripping, that framework artifact wins the "first
 * divergence" on every event and masks the real mismatch (observed on all
 * first-generation production events).
 */
export function stripScripts(html: string): string {
  return html.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');
}

/**
 * Removes `<style>` elements (tags and body) from a markup string.
 *
 * Like scripts, stylesheets are never user-visible content, and third-party
 * widgets inject them into `<body>` between the SSR snapshot and hydration —
 * Cookie Information's `<style id="consent-custom-styles">` lands as the first
 * element in `<body>`, winning the "first divergence" outright (US-11342).
 */
export function stripStyles(html: string): string {
  return html.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '');
}

/**
 * Removes HTML comments from a markup string.
 *
 * React only emits comment nodes when rendering to a string: `<!-- -->`
 * separates adjacent text children (`{n}<!-- --> / <!-- -->{total}`) and
 * `<!--$-->`/`<!--/$-->` mark Suspense boundaries. The client recovery render
 * builds the DOM through `createElement`, so it produces none of them — every
 * such comment is a guaranteed false divergence (US-11342).
 */
export function stripComments(html: string): string {
  return html.replace(/<!--[\s\S]*?-->/g, '');
}

/**
 * A tag with at least one attribute: name, the attribute run, and any trailing
 * self-closing slash. Quoted values are consumed whole, so a `>` inside an
 * attribute value (`aria-label="1 > 2"`) doesn't terminate the match early.
 */
const TAG_WITH_ATTRIBUTES =
  /<([a-zA-Z][^\s/>]*)((?:\s+[^\s=/>]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'>]+))?)+)\s*(\/?)>/g;

const ATTRIBUTE = /([^\s=/>]+)(?:\s*=\s*("[^"]*"|'[^']*'|[^\s"'>]+))?/g;

/**
 * Stands in for a Suspense boundary the server streamed in as nothing at all
 * (`<!--$--><!--/$-->`, a card with no dates to show, say). React's recovery
 * render can re-suspend it and show a skeleton where the server markup has no
 * element to pair it with, so {@link firstMarkupDivergence} needs to know a
 * boundary was there once comments are gone. Either side can carry it: when
 * React recovers a single boundary, the rest of the DOM keeps the server's
 * comments. NUL never occurs in serialized markup.
 */
const EMPTY_BOUNDARY = '\u0000';

/**
 * Drops every attribute, except `aria-hidden="true"` on an empty element: the
 * shape of a Suspense skeleton, which {@link skeletonPattern} looks for.
 *
 * React raises #418 for mismatched elements and text (`args[]=HTML` /
 * `args[]=text`). It compares attributes only to tell a few elements apart
 * while matching (a `<link>`'s `rel`/`href`, a hidden `<input>`'s `name`), and
 * never reports one that differs. So a differing attribute is almost never the
 * cause we are looking for, only noise in front of it, and React's recovery
 * render keeps producing new kinds. Each used to get its own normalizer:
 * attribute order (React assigns `src`/`srcset` last, masking 202 of 276
 * events, US-11342), inline styles reserialized through the CSSOM, `muted`
 * never reflected back (US-12214), regenerated `useId` and react-aria ids
 * (1,276 of 3,964 events), and then the `op_[0]` class every recovered `Image`
 * starts with because it is first rendered in the browser, which since
 * storefront#1531 put the header logo ahead of the real cause on nearly every
 * event. Dropping them all closes the class; the rare `<link>` or `<input>`
 * mismatch now reads as no divergence, and its component stack still names it.
 *
 * The trailing self-closing slash goes too — it is present in `renderToString`
 * output but not in an `innerHTML` serialization.
 */
export function stripAttributes(html: string): string {
  return html.replace(
    TAG_WITH_ATTRIBUTES,
    (
      match: string,
      tag: string,
      attributes: string,
      _slash: string,
      offset: number
    ) => {
      let end = offset + match.length;
      while (html[end] === EMPTY_BOUNDARY) end++;
      const empty = html.startsWith(`</${tag}>`, end);
      const hidden = [...attributes.matchAll(ATTRIBUTE)].some(([attribute]) =>
        /^aria-hidden\s*=\s*"true"$/i.test(attribute.trim())
      );
      return empty && hidden ? `<${tag} aria-hidden="true">` : `<${tag}>`;
    }
  );
}

/**
 * Whether any attribute *name* (never a value, never text content) in the
 * markup starts with `_mst` — Microsoft Translator's own prefix on the
 * attributes it adds to elements it rewrites.
 *
 * Reuses {@link TAG_WITH_ATTRIBUTES}/{@link ATTRIBUTE} — the same tag/attribute
 * split `stripAttributes` uses — instead of a bare `\s_mst…` regex. A plain
 * boundary match on whitespace/`=`/`>` (the first version of this fix) is
 * anchored on the wrong thing: it can't tell an attribute *name* from page
 * text or an attribute *value* that happens to contain `_mst`, and `_mst` is
 * a short, plausible substring of both (a code snippet, a query param).
 * Walking complete tags and checking only the name half of each attribute
 * makes both false-positive shapes structurally impossible: text content is
 * never inside a tag's attribute run, and a value is captured separately
 * from the name by {@link ATTRIBUTE}'s own group split.
 */
function hasTranslatorAttribute(html: string): boolean {
  for (const tag of html.matchAll(TAG_WITH_ATTRIBUTES)) {
    const attributesRun = tag[2] ?? '';
    for (const [, name] of attributesRun.matchAll(ATTRIBUTE)) {
      if (name && /^_mst/i.test(name)) return true;
    }
  }
  return false;
}

/**
 * Whether the markup carries machine-translation artifacts (US-11224).
 *
 * In-browser page translators rewrite the DOM between SSR and hydration, so
 * the resulting #418s are expected and unfixable on our side. Fingerprints
 * observed in production: Microsoft Translator (Edge) scatters `_mst*`
 * attributes over translated elements — `_msttexthash`/`_msthidden` were the
 * only two names originally matched, but `_mstaria-label` also turns up
 * (US-12214, 12 events across 8 domains) and there is no reason to think the
 * set is closed, so {@link hasTranslatorAttribute} matches the `_mst` prefix
 * instead of an explicit name list. Google Translate wraps text nodes in
 * nested `<font dir="auto" style="vertical-align:inherit">` elements.
 */
export function hasTranslationMarkers(html: string): boolean {
  return (
    hasTranslatorAttribute(html) ||
    /<font\b[^>]*vertical-align:\s*inherit/i.test(html)
  );
}

/**
 * Canonicalizes markup so that only user-visible differences survive: strips
 * `<script>` ({@link stripScripts}), `<style>` ({@link stripStyles}), comment
 * nodes ({@link stripComments}) and attributes ({@link stripAttributes}),
 * keeping a marker where a boundary streamed in empty ({@link EMPTY_BOUNDARY}).
 *
 * Scripts and styles go first so a `-->` inside their body can't be mistaken
 * for the end of a comment.
 */
function canonicalize(html: string): string {
  return stripAttributes(
    stripComments(
      stripStyles(stripScripts(html)).replaceAll(
        '<!--$--><!--/$-->',
        EMPTY_BOUNDARY
      )
    )
  );
}

/**
 * An empty element flagged `aria-hidden="true"` — Leaf's shape for a
 * Suspense fallback skeleton. Anchored on the opening tag being immediately
 * followed by its own closing tag (no children), so an aria-hidden element
 * that has real content — a genuine divergence — is never matched.
 */
function skeletonPattern(tagName: string): RegExp {
  return new RegExp(
    `^<${tagName}\\b[^>]*\\baria-hidden="true"[^>]*></${tagName}>`,
    'i'
  );
}

/**
 * The offset immediately after the closing tag that matches the opening tag
 * at `tagStart`, tracking nesting depth so an inner element of the same name
 * doesn't close the match early. Returns null if the markup is truncated
 * before a match is found (observed on Dash0 excerpts, which cap length).
 */
function findMatchingCloseTagEnd(
  html: string,
  tagStart: number,
  tagName: string
): number | null {
  const tagBoundary = new RegExp(
    `<${tagName}\\b[^>]*(/?)>|</${tagName}>`,
    'gi'
  );
  tagBoundary.lastIndex = tagStart;
  let depth = 0;
  let match: RegExpExecArray | null;
  while ((match = tagBoundary.exec(html))) {
    if (match[0].charAt(1) === '/') {
      depth--;
      if (depth === 0) return tagBoundary.lastIndex;
    } else if (match[1] !== '/') {
      depth++;
    }
  }
  return null;
}

/**
 * Whether the divergence at `ssrIndex` (in `ssr`) falls inside an element
 * whose client-side counterpart is an empty Suspense skeleton (see
 * {@link skeletonPattern}). `ssrPos`/`clientPos` mark the start of the region
 * currently under comparison — everything before `ssrIndex` in that region is
 * identical between `ssr` and `client`, so the enclosing tag's start in
 * `client` is at the same *offset from `clientPos`* as `tagStart` is from
 * `ssrPos`, even though the absolute indices differ once a previous skeleton
 * has already been skipped.
 *
 * Returns the offsets to resume comparison from on both sides, skipping the
 * entire matched element — the real content on the SSR side (found via
 * {@link findMatchingCloseTagEnd}) and the empty skeleton on the client side.
 */
function matchSkeletonDivergence(
  ssr: string,
  client: string,
  ssrPos: number,
  clientPos: number,
  ssrIndex: number
): { ssrEnd: number; clientEnd: number } | null {
  const tagStart = ssr.lastIndexOf('<', ssrIndex);
  if (tagStart === -1 || tagStart < ssrPos) return null;

  // The divergence must sit inside this tag's attribute list — if the tag
  // already closed before the divergence point, it's unrelated (e.g. plain
  // text content differs), not a skeleton candidate.
  const tagCloses = ssr.indexOf('>', tagStart);
  if (tagCloses !== -1 && tagCloses < ssrIndex) return null;

  const tagNameMatch = /^<([a-zA-Z][\w-]*)/.exec(ssr.slice(tagStart));
  const tagName = tagNameMatch?.[1];
  if (!tagName) return null;

  const clientTagStart = clientPos + (tagStart - ssrPos);
  const skeletonMatch = skeletonPattern(tagName).exec(
    client.slice(clientTagStart)
  );
  if (!skeletonMatch) return null;

  const ssrEnd = findMatchingCloseTagEnd(ssr, tagStart, tagName);
  if (ssrEnd === null) return null;

  return { ssrEnd, clientEnd: clientTagStart + skeletonMatch[0].length };
}

/** Where an empty skeleton starting at `index` in `html` ends, if one does. */
function emptySkeletonEnd(html: string, index: number): number | null {
  const tagName = /^<([a-zA-Z][\w-]*)/.exec(html.slice(index))?.[1];
  if (!tagName) return null;
  const skeleton = skeletonPattern(tagName).exec(html.slice(index));
  return skeleton ? index + skeleton[0].length : null;
}

/**
 * The first index (independently, in each string) from `ssrPos`/`clientPos`
 * onward where `ssr` and `client` differ, or null if the shorter side's
 * remainder is a match all the way to the end of both strings.
 */
function firstDivergenceFrom(
  ssr: string,
  client: string,
  ssrPos: number,
  clientPos: number
): { ssrIndex: number; clientIndex: number } | null {
  const remaining = Math.min(ssr.length - ssrPos, client.length - clientPos);
  let offset = 0;
  while (
    offset < remaining &&
    ssr[ssrPos + offset] === client[clientPos + offset]
  ) {
    offset++;
  }
  if (
    offset === remaining &&
    ssr.length - ssrPos === client.length - clientPos
  ) {
    return null;
  }
  return { ssrIndex: ssrPos + offset, clientIndex: clientPos + offset };
}

/**
 * The first point where the two markup strings diverge, with enough
 * surrounding context to recognise the content ("17.30" vs "05.30 PM", a
 * Google-Translate `<font>` wrapper, …). Both sides are canonicalized first
 * (see {@link canonicalize}) so that serialization artifacts of React's
 * recovery render don't win the comparison. Returns null when only that noise
 * differs, or when the recovered client markup is empty — observed on Safari
 * checkout events where the diff fired mid-recovery, and an
 * "everything vs nothing" divergence names no mismatch.
 *
 * Suspense fallbacks are a further case of the same noise (US-12214, ~110
 * events — the second-largest #418 bucket): by the time the diff runs,
 * React's recovery render has re-suspended a boundary whose loader data is
 * pending again, so an empty skeleton (see {@link skeletonPattern}), in place
 * of the server's content or where the server had nothing, wins the "first
 * divergence" over the real cause sitting later in the DOM. Each
 * skeleton found is skipped and counted in `skipped` rather than silently
 * dropped, so the result always distinguishes three states: no divergence at
 * all (`null`), a real divergence — possibly found only after skipping past
 * one or more skeletons (`ssr`/`client` present, `skipped` may be > 0), and a
 * divergence that consists *only* of skeleton noise, with nothing left to
 * report once every skeleton is skipped (`skipped > 0`, `ssr`/`client`
 * absent). A single `skipped` count carries this distinction on its own —
 * whether it's paired with `ssr`/`client` or not — so no separate
 * `hydration.cause` tag is needed for the "skeleton was the only difference"
 * case.
 */
export function firstMarkupDivergence(
  ssrHtml: string,
  clientHtml: string
): { ssr?: string; client?: string; skipped: number } | null {
  const ssr = canonicalize(ssrHtml);
  const client = canonicalize(clientHtml);
  if (ssr === client) return null;
  if (client.trim() === '') return null;

  let ssrPos = 0;
  let clientPos = 0;
  let skipped = 0;

  for (;;) {
    const divergence = firstDivergenceFrom(ssr, client, ssrPos, clientPos);
    if (!divergence) {
      return skipped > 0 ? { skipped } : null;
    }

    const { ssrIndex, clientIndex } = divergence;
    const skeleton = matchSkeletonDivergence(
      ssr,
      client,
      ssrPos,
      clientPos,
      ssrIndex
    );
    if (skeleton) {
      ssrPos = skeleton.ssrEnd;
      clientPos = skeleton.clientEnd;
      skipped++;
      continue;
    }

    if (ssr[ssrIndex] === EMPTY_BOUNDARY) {
      // A boundary the server streamed in empty: the client has nothing there
      // either, or the skeleton it re-suspended to — unless the server has the
      // same element right after it, which is then no skeleton.
      const skeletonEnd = emptySkeletonEnd(client, clientIndex);
      const isSkeleton =
        skeletonEnd !== null &&
        !ssr.startsWith(client.slice(clientIndex, skeletonEnd), ssrIndex + 1);
      ssrPos = ssrIndex + 1;
      clientPos = isSkeleton ? skeletonEnd : clientIndex;
      if (isSkeleton) skipped++;
      continue;
    }
    if (client[clientIndex] === EMPTY_BOUNDARY) {
      ssrPos = ssrIndex;
      clientPos = clientIndex + 1;
      continue;
    }

    return {
      ssr: ssr
        .substring(Math.max(0, ssrIndex - 120), ssrIndex + 180)
        .replaceAll(EMPTY_BOUNDARY, ''),
      client: client
        .substring(Math.max(0, clientIndex - 120), clientIndex + 180)
        .replaceAll(EMPTY_BOUNDARY, ''),
      skipped,
    };
  }
}
